import type { Site, SiteContactForm } from '../../services/siteService';

/**
 * ตรรกะฟอร์มโครงการ (ไม่มี UI) — ย้ายจากโมดัลใน SitePage (28 ต.ค. 2026) ให้หน้าดู (SiteViewPage) และ
 * หน้าเพิ่ม/แก้ไข (SiteFormPage) ใช้ mapper ชุดเดียว ไม่ต้องคัดลอก
 *
 * 🔴 รหัส 3 คอลัมน์ (docs/reference/DATABASE_SCHEMA.md ข้อ sites — อย่าสลับกัน):
 *   activation_code = "รหัส Activate" · customer_number = "รหัสลูกค้า" (ฟอร์มเขียนลงคอลัมน์นี้) ·
 *   customer_code = legacy ที่ K-Visit ใช้จับคู่ (ฟอร์มไม่เขียนทับ — service ไม่ส่ง key นี้)
 *   ช่อง "รหัสลูกค้า" prefill จาก customer_number ก่อน ไม่มีค่อยใช้ customer_code (พฤติกรรมเดิมของโมดัล)
 */
export interface SiteFormState {
  code: string;
  name: string;
  display_name: string;
  app_name: string;
  company_id: number | string;
  status: string;
  address: string;
  customer_number: string;
  contactPerson: string;
  phone: string;
  email: string;
  activation_code: string;
  k_connect_enabled: boolean;
  telegram_enabled: boolean;
  project_phone: string;
  contact_notes: string;
}

/** ฟอร์มเปล่าตอน "เพิ่มโครงการใหม่" — ค่าเดียวกับ handleAddSite ของโมดัลเดิม */
export const emptySiteForm = (code = ''): SiteFormState => ({
  code,
  name: '',
  display_name: '',
  app_name: '',
  company_id: '',
  status: 'active',
  address: '',
  customer_number: '',
  contactPerson: '',
  phone: '',
  email: '',
  activation_code: '',
  k_connect_enabled: false,
  telegram_enabled: false,
  project_phone: '',
  contact_notes: '',
});

/** โครงการจาก API → ค่าในฟอร์ม (ตรงกับ handleViewSite/handleEditSite ของโมดัลเดิม) */
export const siteToForm = (site: Site): SiteFormState => ({
  code: site.code,
  name: site.name,
  display_name: site.display_name || '',
  app_name: site.app_name || '',
  company_id: site.company_id,
  status: site.status || 'active',
  address: site.address || '',
  customer_number: site.customer_number || site.customer_code || '',
  contactPerson: site.contactPerson || '',
  phone: site.phone || '',
  email: site.email || '',
  activation_code: site.activation_code || '',
  k_connect_enabled: site.k_connect_enabled || false,
  telegram_enabled: site.telegram_enabled || false,
  project_phone: site.project_phone || '',
  contact_notes: site.contact_notes || '',
});

export const siteToContacts = (site: Site): SiteContactForm[] =>
  (site.contacts || []).map((c) => ({
    contact_type: c.contact_type,
    name: c.name || '',
    phone: c.phone || '',
    line_id: c.line_id || '',
    notes: c.notes || '',
    is_primary: c.is_primary || false,
  }));

/** ตรวจฝั่ง FE — key = name ของช่อง (ใช้ focus/scroll หาช่องแรกที่ผิด) */
export const validateSiteForm = (formData: SiteFormState): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!formData.company_id) errors.company_id = 'กรุณาเลือกบริษัท';
  if (!formData.code.trim()) errors.code = 'กรุณาใส่รหัสโครงการ';
  if (!formData.name.trim()) errors.name = 'กรุณาใส่ชื่อโครงการ';
  return errors;
};

/** payload ของ create/update — ทุก key มาจากช่องที่อยู่บนฟอร์ม (ไม่มีช่องที่ฟอร์มไม่แสดง) */
export const buildSitePayload = (formData: SiteFormState, contacts: SiteContactForm[]) => ({
  code: formData.code,
  name: formData.name,
  display_name: formData.display_name || undefined,
  app_name: formData.app_name || undefined,
  company_id: formData.company_id ? parseInt(formData.company_id.toString()) : 0,
  address: formData.address,
  customer_number: formData.customer_number || undefined,
  contactPerson: formData.contactPerson,
  phone: formData.phone,
  email: formData.email,
  status: formData.status || 'active',
  activation_code: formData.activation_code || undefined,
  k_connect_enabled: formData.k_connect_enabled,
  telegram_enabled: formData.telegram_enabled,
  project_phone: formData.project_phone || undefined,
  contact_notes: formData.contact_notes || undefined,
  contacts,
});

/** token If-Match ของ PUT (P-S468-1) — sites API คืน snake_case */
export const siteIfMatch = (site: Site): string | undefined => site.updated_at || site.created_at || undefined;

/** ชื่อฟิลด์ภาษาไทยของไทม์ไลน์ประวัติ (key = คอลัมน์ backend ใน details.changes / "fields: …") */
export const SITE_FIELD_LABELS: Record<string, string> = {
  code: 'รหัสโครงการ',
  name: 'ชื่อโครงการ',
  display_name: 'ชื่อใช้งานจริงที่ Activate ในระบบ',
  app_name: 'ชื่อที่ใช้ในแอพ',
  company_id: 'บริษัท',
  status: 'สถานะ',
  address: 'ที่อยู่',
  customer_number: 'รหัสลูกค้า',
  customer_code: 'รหัสลูกค้า (เดิม)',
  activation_code: 'รหัส Activate',
  contact_person: 'ผู้ติดต่อ',
  phone: 'เบอร์โทรศัพท์',
  email: 'อีเมล',
  k_connect_enabled: 'K-Connect',
  telegram_enabled: 'Telegram',
  project_phone: 'เบอร์โทรโครงการ/นิติบุคคล',
  contact_notes: 'ข้อมูลติดต่อ รปภ./ผู้ดูแล',
  contacts: 'รายชื่อผู้ติดต่อโครงการ',
};

export const SITE_STATUS_LABEL = (status?: string | null): string =>
  status === 'active' ? 'ใช้งาน' : status === 'inactive' ? 'ไม่ใช้งาน' : status || 'ไม่ระบุ';
